<template>
    <div class="container">
        <v-title>
            Add Customer
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'lists', params: {segmentId: 'read-only-customers'}}"></v-cancel>
                <r-button type="primary" @click="save()" :loading="isLoading">Save Customer</r-button>
            </div>
        </v-title>
        <v-error-messages
            :has-frontend-errors="$v.customer.$error || $v.paymentInstrument.$error || hasErrors"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.customer.id.resourceId"><span>The customer ID cannot contain spaces or special characters other than <strong>@ ~ _ -</strong> and should be less than <strong>50</strong> characters long</span>
            </li>
            <template v-if="customer.defaultPaymentInstrument.method === 'ach'">
                <li class="list-item" v-if="!$v.paymentInstrument.accountNumber.required">Customer bank account
                    account number is required
                </li>
                <li class="list-item" v-if="!$v.paymentInstrument.routingNumber.required">Customer bank account routing
                    number is required
                </li>
            </template>
            <template v-if="customer.defaultPaymentInstrument.method === 'payment-card'">
                <li class="list-item" v-if="!$v.paymentInstrument.pan.required">Customer payment card number is
                    required
                </li>
                <li class="list-item" v-if="!$v.paymentInstrument.pan.mustBeValidPan">Customer payment card number must
                    be valid
                </li>
                <li class="list-item" v-if="!$v.paymentInstrument.expMonth.required">Customer payment card expiry month
                    is required
                </li>
                <li class="list-item" v-if="!$v.paymentInstrument.expYear.required">Customer payment card expiry year is
                    required
                </li>
                <li class="list-item" v-if="!$v.paymentInstrument.cvv.required">Customer payment card CVV is
                    required
                </li>
            </template>
            <template v-if="hasErrors">
                <li class="list-item" v-for="error in validationAggregator.aggregatedResult">{{error.name}}</li>
            </template>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Customer Info</h2>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="ID"
                                :auto-focus="true"
                                v-model="customer.id"
                                :validate="$v.customer.id"
                                help-text="The ID must be unique and is used to identify the customer in our API."
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Website Id"
                                :options="websitesIds"
                                v-model="customer.websiteId"
                                placeholder="Select a website"
                            />
                        </div>
                        <contact-form
                            :value="customer.primaryAddress"
                            :validation-aggregator="validationAggregator"
                            :validate="$v.customer.primaryAddress"
                            @input="updatePrimaryAddress"/>
                        <div class="grid-item grid-item-full">
                            <v-custom-fields-manager
                                resource-type="customers"
                                v-model="customer.customFields"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Default Payment Instrument</h2>
            <p slot="description" class="text-muted">Create a default payment instrument for your customer. The default
                payment instrument will be used for autopay when creating a subscription.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <select-payment-method
                                :methods="paymentInstrumentOptions"
                                @input="clearPaymentInstrument"
                                v-model="customer.defaultPaymentInstrument.method"/>
                        </div>
                    </div>
                </div>
                <div class="tile-content" v-if="customer.defaultPaymentInstrument.method !== 'none'">
                    <component
                        :is="selectedPaymentInstrumentMethod"
                        @input="updatePaymentInstrument"
                        :custom-fields="paymentCardFields"
                        :validate="$v.paymentInstrument"
                        @card-type-update="onCardTypeUpdate"
                        class="grid-item grid-item-half grid-item-break-half"/>
                    <template v-if="customer.defaultPaymentInstrument.method === 'payment-card'">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <label class="field-label stack-s">Billing Address</label>
                                <v-radio
                                    group="accessType"
                                    label="Same as above"
                                    :value="false"
                                    class="stack-m"
                                    :model="newPaymentCardBillingAddress"
                                    @update="updatePaymentCardBillingAddress"
                                />
                                <v-radio
                                    group="accessType"
                                    label="Create one"
                                    :value="true"
                                    :model="newPaymentCardBillingAddress"
                                    @update="updatePaymentCardBillingAddress"
                                />
                            </div>
                        </div>
                    </template>
                    <template v-if="newPaymentCardBillingAddress">
                        <div class="grid-content">
                            <contact-form
                                :value="billingAddress"
                                :validation-aggregator="validationAggregator"
                                :validate="$v.billingAddress"
                                @input="updateBillingAddress"/>
                        </div>
                    </template>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'lists', params: {segmentId: 'read-only-customers'}}"></v-cancel>
                <r-button type="primary" @click="save()" :loading="isLoading">Save Customer</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import {
        vCancel, vErrorMessages, vFormTile, vRadio, vSelect,
    } from '@/components/form';
    import CustomerModel from '@/common/models/customer-model';
    import ContactModel from '@/common/models/contact-model';
    import PaymentInstrumentModel from '@/common/models/payment-instrument-model';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import vCustomFieldsManager from '@/components/custom-fields/v-custom-fields-manager';
    import ValidationAggregator from '@/common/validation/validation-aggregator';
    import _ from '@/common/helpers';
    import {email, numeric, required} from 'vuelidate/lib/validators';
    import resourceId from '@/components/vuelidate/validators/resource-id';
    import ContactForm from './components/contact-form';
    import SelectPaymentMethod from './components/select-payment-method';
    import AchForm from './components/ach-form';
    import PaymentCardForm from './components/payment-card-form';
    import RESOURCES from '@/common/auth/definitions/resources';
    import {DEFAULT_CVV_LENGTH, getCvvLength} from '@/common/format/pan-format';

    export default {
        name: 'customer-form',
        mixins: [FormLoaderMixin],
        components: {
            vTitle,
            vFormTile,
            vCancel,
            vSelect,
            vRadio,
            vCustomFieldsManager,
            vErrorMessages,
            ContactForm,
            SelectPaymentMethod,
            AchForm,
            PaymentCardForm,
        },
        data() {
            return {
                customer: new CustomerModel({defaultPaymentInstrument: {method: 'none'}}),
                billingAddress: new ContactModel(),
                paymentInstrumentOptions: [
                    {
                        label: 'None',
                        value: 'none',
                    },
                    {
                        label: 'Payment Card',
                        value: PaymentInstrumentModel.Methods.paymentCard,
                    },
                    {
                        label: 'Bank Account',
                        value: PaymentInstrumentModel.Methods.ach,
                    },
                ],
                paymentInstrument: {},
                paymentCardFields: null,
                contactsFields: null,
                newPaymentCardBillingAddress: !_.isEmpty(this.value),
                validationAggregator: new ValidationAggregator(),
                hasErrors: false,
                cvvLength: DEFAULT_CVV_LENGTH,
                websitesIds: [],
            };
        },
        validations() {
            const validation = {
                customer: {
                    id: {resourceId},
                    primaryAddress: {
                        phoneNumbers: {
                            $each: {
                                value: {
                                    required,
                                },
                            },
                        },
                        emails: {
                            $each: {
                                value: {
                                    required,
                                    email,
                                },
                            },
                        },
                    },
                },
                paymentInstrument: {},
                billingAddress: {
                    phoneNumbers: {
                        $each: {
                            value: {
                                required,
                            },
                        },
                    },
                    emails: {
                        $each: {
                            value: {
                                required,
                                email,
                            },
                        },
                    },
                },
            };
            if (this.customer.defaultPaymentInstrument.method === PaymentInstrumentModel.Methods.ach) {
                validation.paymentInstrument = {
                    accountNumber: {required},
                    routingNumber: {required},
                };
            } else if (this.customer.defaultPaymentInstrument.method
                === PaymentInstrumentModel.Methods.paymentCard) {
                validation.paymentInstrument = {
                    pan: {
                        required,
                        mustBeValidPan(value) {
                            const pan = this.$filters.pan(
                                {
                                    pan: value,
                                    concat: false,
                                },
                            );
                            return pan.valid;
                        },
                    },
                    cvv: {
                        numeric,
                        cardBasedLength: _.lengthEquals(this.cvvLength),
                    },
                    expYear: {required},
                    expMonth: {required},
                };
            }
            return validation;
        },
        computed: {
            selectedPaymentInstrumentMethod() {
                const {method} = this.customer.defaultPaymentInstrument;
                return method === 'ach' ? 'ach-form' : 'paymentCard-form';
            },
            isDataRequired() {
                return this.isFormUpdating || !this.isDataReady;
            },
        },
        methods: {
            onCardTypeUpdate({type}) {
                this.cvvLength = getCvvLength(type);
            },
            beforeSaveDelegate() {
                this.hasErrors = this.validationAggregator.validateAggregated();
                return !this.hasErrors;
            },
            updatePrimaryAddress(value) {
                this.customer.primaryAddress = value;
            },
            updateBillingAddress(value) {
                this.billingAddress = value;
            },
            updatePaymentCardBillingAddress(value) {
                this.newPaymentCardBillingAddress = value;
            },
            async create() {
                if (!this.$v.paymentInstrument.$error) {
                    const paymentInstrumentPayload = {data: null};
                    const customerPayload = this.customer.getPayload();
                    if (this.customer.defaultPaymentInstrument.method !== 'none') {
                        paymentInstrumentPayload.data = this.paymentInstrument;
                    }
                    if (this.customer.defaultPaymentInstrument.method
                        === PaymentInstrumentModel.Methods.ach) {
                        paymentInstrumentPayload.data.address = this.customer.primaryAddress.getPayload();
                    }
                    if (this.customer.defaultPaymentInstrument.method
                        === PaymentInstrumentModel.Methods.paymentCard) {
                        paymentInstrumentPayload.data.billingAddress = this.newPaymentCardBillingAddress
                            ? this.billingAddress
                            : this.customer.primaryAddress;
                    }
                    try {
                        const {fields} = await this.$api(({customers}) => customers.create(customerPayload));
                        if (this.customer.defaultPaymentInstrument.method !== 'none') {
                            // create payment card
                            try {
                                // customer successfully created
                                // we can't leave the user on customer-create page in case if any of next requests will failed
                                await this.createPaymentInstrument({paymentInstrumentPayload, customer: fields});
                            } catch (err) {
                                this.$toast.fromError(err, 'Can\'t attach a payment method');
                                this.$router.push({
                                    name: 'customer-details',
                                    params: {id: fields.id},
                                    query: {newRecord: true},
                                });
                                return;
                            }
                        }
                        this.$router.push({
                            name: 'customer-details',
                            params: {id: fields.id},
                            query: {newRecord: true},
                        });
                        this.customer = new CustomerModel({defaultPaymentInstrument: {method: 'none'}});
                    } catch (error) {
                        this.$toast.fromError(error, 'Customer could not be saved');
                        throw error;
                    }
                }
            },
            async createPaymentInstrument({paymentInstrumentPayload, customer}) {
                paymentInstrumentPayload.data.customerId = customer.id;
                const paymentInstrument = await this.$api(({paymentCards, bankAccounts}) => {
                    const target = this.customer.defaultPaymentInstrument.method
                        === PaymentInstrumentModel.Methods.paymentCard
                        ? paymentCards : bankAccounts;
                    return target.create(paymentInstrumentPayload);
                });
                const defaultPaymentInstrumentId = this.customer.defaultPaymentInstrument.method
                    === PaymentInstrumentModel.Methods.paymentCard
                    ? 'paymentCardId' : 'bankAccountId';
                const defaultPaymentInstrument = {
                    method: this.customer.defaultPaymentInstrument.method,
                    [defaultPaymentInstrumentId]: paymentInstrument.fields.id,
                };
                const defaultPaymentInstrumentPayload = {
                    id: customer.id,
                    data: {
                        ...customer,
                        defaultPaymentInstrument,
                    },
                };
                await this.$api(({customers}) => customers.update(defaultPaymentInstrumentPayload));
            },
            async fetchData() {
                const paymentCardsCustomFieldsParams = {
                    resource: RESOURCES.paymentCards,
                };
                const contactsCustomFieldsParams = {
                    resource: RESOURCES.contacts,
                };
                try {
                    const [
                        {items: paymentCardsCustomFields},
                        {items: contactsCustomFields},
                        {items: websitesItems},
                    ] = await this.$api(({
                        customFields,
                        websites,
                    }) => Promise.all([
                        customFields.allowedOnly.getAll(paymentCardsCustomFieldsParams),
                        customFields.allowedOnly.getAll(contactsCustomFieldsParams),
                        websites.allowedOnly.getAll(),
                    ]));
                    this.paymentCardFields = paymentCardsCustomFields;
                    this.contactsFields = contactsCustomFields;
                    this.websitesIds = websitesItems.map(item => item.fields.id);
                    if (this.websitesIds.length > 0) {
                        const [firstWebsiteId] = this.websitesIds;
                        this.customer.websiteId = firstWebsiteId;
                    }
                } catch (error) {
                    this.$toast.fromError(error, 'Something went wrong, please contact support');
                }
            },
            updatePaymentInstrument(value) {
                this.paymentInstrument = value;
            },
            clearPaymentInstrument() {
                this.paymentInstrument = {};
                this.billingAddress = new ContactModel();
                this.$v.$reset();
            },
        },
    };
</script>
